import { FunnelFieldLabels } from '../../../../constants/deprecated_fieldsManagementConsts';
import { checkValueInRange } from '../../../../lib/checkValuesInRange';
import { ConfigurationType } from '../../dependencies/types';
import { escapeRegexp } from '../../../../lib/escapeRegex';
import isString from 'lodash/isString';
import {
  activeFilterOptions,
  ActiveState,
  apexConfigurationTypes,
} from '../../activity-state/helpers';
import {
  ConfigurationItem,
  FieldMetadataRecordProperties,
} from '../../dependencies/DependenciesTypes';
import { isItemActive } from '../../helpers';
import { uniqueByKey } from '../../../../lib/uniqueByKey';
import { DocumentationFiltersTypesMap, DocumentationValuedFilters } from './filterTypes';
import { FilterComponentType } from '../../../common/filters/filters-renderer/types';
import { getDeployedVersions } from '../filters/utils';
import { getActionFilterItems } from '../../../Automations/filters/helpers';
import { DedupMatchingType } from '../../../../types/enums/DedupMatchingType';
import {
  isConfigurationItemUsedByRecordType,
  isConfigurationItemUsedByStep,
} from '../../pills/pillsUtils';

const USAGE_SEPARATOR = '-';

const fieldUsageOptions = [
  {
    label: '0% - 25%',
    value: `0${USAGE_SEPARATOR}25`,
  },
  {
    label: '26% - 50%',
    value: `26${USAGE_SEPARATOR}50`,
  },
  {
    label: '51% - 75%',
    value: `51${USAGE_SEPARATOR}75`,
  },
  {
    label: '76% - 100%',
    value: `76${USAGE_SEPARATOR}100`,
  },
];

const automationsTypeOptions = [
  {
    label: 'Flow',
    value: ConfigurationType.flows,
  },
  {
    label: 'Approval Process',
    value: ConfigurationType.approvalProcesses,
  },
  {
    label: 'Process Builder',
    value: ConfigurationType.processBuilderFlows,
  },
  {
    label: 'Workflow Rule',
    value: ConfigurationType.workflowRules,
  },
];

export const getFieldsTabFilters = (fields: FieldMetadataRecordProperties[]) => {
  const fieldTypes = uniqueByKey(
    fields,
    (field) => field.type,
    (field) => ({
      label: FunnelFieldLabels[field.type as SweepFieldTypes] ?? field.type,
      value: field.type,
    }),
  );

  return {
    [DocumentationFiltersTypesMap.fieldTypes]: {
      type: FilterComponentType.MULTI_SELECT,
      items: fieldTypes,
      label: 'Type',
    },
    [DocumentationFiltersTypesMap.utilization]: {
      type: FilterComponentType.MULTI_SELECT,
      items: fieldUsageOptions,
      label: 'Utilization',
    },
  };
};

export const getApexTabFilters = (
  recordTypesItems: { value: string; label: string }[],
  stepNamesItems: { value: string; label: string }[],
) => {
  return {
    [DocumentationFiltersTypesMap.apexTypes]: {
      type: FilterComponentType.MULTI_SELECT,
      items: apexConfigurationTypes,
      label: 'Type',
    },
    [DocumentationFiltersTypesMap.recordTypes]: {
      type: FilterComponentType.MULTI_SELECT,
      items: recordTypesItems,
      label: 'Record Types',
    },
    [DocumentationFiltersTypesMap.steps]: {
      type: FilterComponentType.MULTI_SELECT,
      items: stepNamesItems,
      label: 'Steps',
    },
    [DocumentationFiltersTypesMap.activeState]: {
      type: FilterComponentType.SINGLE_SELECT,
      items: activeFilterOptions,
      label: 'Show',
    },
  };
};

export const getDocumentationAutomationFilters = (
  automations: AutomationStructureNew[],
  recordTypesItems: { value: string; label: string }[],
  stepNamesItems: { value: string; label: string }[],
) => ({
  [DocumentationFiltersTypesMap.actionTypes]: {
    type: FilterComponentType.MULTI_SELECT,
    label: 'Action',
    items: getActionFilterItems(getDeployedVersions(automations) ?? []),
  },
  [DocumentationFiltersTypesMap.recordTypes]: {
    type: FilterComponentType.MULTI_SELECT,
    items: recordTypesItems,
    label: 'Record Types',
  },
  [DocumentationFiltersTypesMap.steps]: {
    type: FilterComponentType.MULTI_SELECT,
    items: stepNamesItems,
    label: 'Steps',
  },
});

export const statusFilterItems = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export const dedupMatchingFilters = ({
  recordTypesItems,
}: {
  recordTypesItems: { value: string; label: string }[];
}) => ({
  [DocumentationFiltersTypesMap.operations]: {
    type: FilterComponentType.MULTI_SELECT,
    label: 'Operations',
    items: [
      //if new added DEDUP_OPERATIONS should be updated
      { value: 'deduplication_section', label: 'Deduplication', isSectionItem: true },
      { value: DedupMatchingType.LEAD_TO_LEAD_DEDUP, label: 'Lead to Lead' },
      { value: DedupMatchingType.CONTACT_TO_CONTACT_DEDUP, label: 'Contact to Contact' },
      { value: DedupMatchingType.ACCOUNT_TO_ACCOUNT_DEDUP, label: 'Account to Account' },
      { value: DedupMatchingType.LEAD_TO_CONTACT_DEDUP, label: 'Lead to Contact' },
      //if new added DEDUP_OPERATIONS should be updated
      { value: 'matching_section', label: 'Matching', isSectionItem: true },
      { value: DedupMatchingType.LEAD_TO_LEAD_MATCHING, label: 'Lead to Lead' },
      { value: DedupMatchingType.LEAD_TO_ACCOUNT_MATCHING, label: 'Lead to Account' },
    ],
  },
  [DocumentationFiltersTypesMap.status]: {
    type: FilterComponentType.MULTI_SELECT,
    label: 'Status',
    items: statusFilterItems,
  },
  [DocumentationFiltersTypesMap.recordTypes]: {
    type: FilterComponentType.MULTI_SELECT,
    items: recordTypesItems,
    label: 'Record Types',
  },
});

export const getRelatedObjectsFilters = (
  relatedObjectsOptions: { label: string; value: string }[],
  configurationTypeOptions: { label: string; value: string }[],
) => {
  return {
    [DocumentationFiltersTypesMap.relatedObject]: {
      type: FilterComponentType.MULTI_SELECT,
      items: relatedObjectsOptions,
      label: 'Related object',
    },
    [DocumentationFiltersTypesMap.configurationType]: {
      type: FilterComponentType.MULTI_SELECT,
      items: configurationTypeOptions,
      label: 'Type',
    },
    [DocumentationFiltersTypesMap.activeState]: {
      type: FilterComponentType.SINGLE_SELECT,
      items: activeFilterOptions,
      label: 'Show',
    },
  };
};

export const getLayoutsTabFilters = (recordTypesItems: { value: string; label: string }[]) => {
  return {
    [DocumentationFiltersTypesMap.recordTypes]: {
      type: FilterComponentType.MULTI_SELECT,
      items: recordTypesItems,
      label: 'Record Types',
    },
  };
};

export const getRecordTypesTabFilters = () => {
  return {
    [DocumentationFiltersTypesMap.activeState]: {
      type: FilterComponentType.SINGLE_SELECT,
      items: activeFilterOptions,
      label: 'Show',
    },
  };
};

export const getSalesforceAutomationsTabFilters = (
  recordTypesItems: { value: string; label: string }[],
  stepNamesItems: { value: string; label: string }[],
) => {
  return {
    [DocumentationFiltersTypesMap.automationTypes]: {
      type: FilterComponentType.MULTI_SELECT,
      items: automationsTypeOptions,
      label: 'Type',
    },
    [DocumentationFiltersTypesMap.recordTypes]: {
      type: FilterComponentType.MULTI_SELECT,
      items: recordTypesItems,
      label: 'Record Types',
    },
    [DocumentationFiltersTypesMap.steps]: {
      type: FilterComponentType.MULTI_SELECT,
      items: stepNamesItems,
      label: 'Steps',
    },

    [DocumentationFiltersTypesMap.activeState]: {
      type: FilterComponentType.SINGLE_SELECT,
      items: activeFilterOptions,
      label: 'Show',
    },
  };
};

export const getValidationRulesTabFilters = (
  recordTypesItems: { value: string; label: string }[],
  stepNamesItems: { value: string; label: string }[],
) => {
  return {
    [DocumentationFiltersTypesMap.recordTypes]: {
      type: FilterComponentType.MULTI_SELECT,
      items: recordTypesItems,
      label: 'Record Types',
    },
    [DocumentationFiltersTypesMap.steps]: {
      type: FilterComponentType.MULTI_SELECT,
      items: stepNamesItems,
      label: 'Steps',
    },
    [DocumentationFiltersTypesMap.activeState]: {
      type: FilterComponentType.SINGLE_SELECT,
      items: activeFilterOptions,
      label: 'Show',
    },
  };
};

export const filterRules = ({
  rules,
  selectedStepValuesData,
  selectedTypeValuesData,
  selectedRecordTypeValuesData,
}: {
  rules: ConfigurationItem[];
  selectedStepValuesData?: DocumentationValuedFilters;
  selectedTypeValuesData?: DocumentationValuedFilters;
  selectedRecordTypeValuesData?: DocumentationValuedFilters;
}) => {
  const selectedStepValues = selectedStepValuesData?.selectedValues;
  const selectedRecordTypeValues = selectedRecordTypeValuesData?.selectedValues;
  const selectedTypeValues = selectedTypeValuesData?.selectedValues;

  if (
    !selectedStepValuesData?.selectedValues.length &&
    !selectedRecordTypeValuesData?.selectedValues.length &&
    !selectedTypeValuesData?.selectedValues.length
  ) {
    return rules;
  }

  return rules.filter((rule) => {
    const isSelectedStage = selectedStepValues?.length
      ? selectedStepValues?.some((stage) => isConfigurationItemUsedByStep(stage)(rule))
      : true;

    const isSelectedRecordType = selectedRecordTypeValues?.length
      ? selectedRecordTypeValues?.some((recordType) =>
          isConfigurationItemUsedByRecordType(recordType)(rule),
        )
      : true;

    const isSelectedType = selectedTypeValues?.length
      ? selectedTypeValues?.includes(rule.type)
      : true;

    return isSelectedStage && isSelectedType && isSelectedRecordType;
  });
};

export const filterSweepAutomations = ({
  automations,
  selectedActionTypesData,
  selectedRecordTypesData,
  selectedStepsData,
}: {
  automations: ConfigurationItem[];
  selectedActionTypesData?: DocumentationValuedFilters;
  selectedRecordTypesData?: DocumentationValuedFilters;
  selectedStepsData?: DocumentationValuedFilters;
}) => {
  const selectedActionTypes = selectedActionTypesData?.selectedValues;
  const selectedRecordTypesValues = selectedRecordTypesData?.selectedValues;
  const selectedStepValues = selectedStepsData?.selectedValues;

  if (
    !selectedActionTypes?.length &&
    !selectedRecordTypesValues?.length &&
    !selectedStepValues?.length
  ) {
    return automations;
  }

  return automations.filter((automation) => {
    const isSelectedActionType = selectedActionTypes?.length
      ? selectedActionTypes.some((actionType) =>
          automation.actions?.includes(actionType as AutomationActionType),
        )
      : true;

    const isSelectedStage = selectedStepValues?.length
      ? selectedStepValues?.some((stage) => isConfigurationItemUsedByStep(stage)(automation))
      : true;

    const isSelectedRecordType = selectedRecordTypesValues?.length
      ? selectedRecordTypesValues?.some((recordType) =>
          isConfigurationItemUsedByRecordType(recordType)(automation),
        )
      : true;

    return isSelectedActionType && isSelectedStage && isSelectedRecordType;
  });
};

export const filterDedupeAndMatching = ({
  automations,
  selectedActionTypesData,
  selectedRecordTypesData,
  selectedStepsData,
}: {
  automations: ConfigurationItem[];
  selectedActionTypesData?: DocumentationValuedFilters;
  selectedRecordTypesData?: DocumentationValuedFilters;
  selectedStepsData?: DocumentationValuedFilters;
}) => {
  const selectedActionTypes = selectedActionTypesData?.selectedValues;
  const selectedRecordTypesValues = selectedRecordTypesData?.selectedValues;
  const selectedStepValues = selectedStepsData?.selectedValues;

  if (
    !selectedActionTypes?.length &&
    !selectedRecordTypesValues?.length &&
    !selectedStepValues?.length
  ) {
    return automations;
  }

  return automations.filter((automation) => {
    const isSelectedActionType = selectedActionTypes?.length
      ? selectedActionTypes.some((actionType) =>
          automation.actions?.includes(actionType as AutomationActionType),
        )
      : true;

    const isSelectedStage = selectedStepValues?.length
      ? selectedStepValues?.some((stage) => isConfigurationItemUsedByStep(stage)(automation))
      : true;

    const isSelectedRecordType = selectedRecordTypesValues?.length
      ? selectedRecordTypesValues?.some((recordType) =>
          isConfigurationItemUsedByRecordType(recordType)(automation),
        )
      : true;

    return isSelectedActionType && isSelectedStage && isSelectedRecordType;
  });
};

export const filterFields = ({
  fields,
  selectedUsageValues,
  selectedTypeValues,
}: {
  fields: ConfigurationItem[];
  selectedUsageValues?: string[];
  selectedTypeValues?: string[];
}) => {
  const selectedTypeValuesHasNoFilter = !selectedTypeValues?.length;
  const selectedUsageValuesHasNoFilter = !selectedUsageValues?.length;

  if (selectedTypeValuesHasNoFilter && selectedUsageValuesHasNoFilter) {
    return fields;
  }

  return fields.filter((field) => {
    const isSelectedFieldType =
      selectedTypeValuesHasNoFilter || selectedTypeValues?.includes(field.type);

    const isInRange =
      selectedUsageValuesHasNoFilter ||
      selectedUsageValues?.some((value) => {
        const usageRangeValues = value.split(USAGE_SEPARATOR);

        return checkValueInRange({
          valueToCheck: field.usage ?? 0,
          bottomValue: Number(usageRangeValues[0]),
          topValue: Number(usageRangeValues[1]),
        });
      });

    return isSelectedFieldType && isInRange;
  });
};

export const filterRelatedObjects = ({
  items,
  selectedActiveState,
  selectedRelatedObjects,
  selectedConfigurationTypes,
  excludedFromReferenceObjectName,
}: {
  items: ConfigurationItem[];
  selectedActiveState?: string;
  selectedRelatedObjects?: DocumentationValuedFilters;
  selectedConfigurationTypes?: DocumentationValuedFilters;
  excludedFromReferenceObjectName: string;
}) => {
  const selectedRelatedObjectsValues = selectedRelatedObjects?.selectedValues;
  const selectedConfigurationTypesValues = selectedConfigurationTypes?.selectedValues;
  const selectedActiveOnly = selectedActiveState
    ? selectedActiveState === ActiveState.activeOnly
    : true;

  if (
    !selectedRelatedObjectsValues?.length &&
    !selectedConfigurationTypesValues?.length &&
    !selectedActiveOnly
  ) {
    return items;
  }

  return items.filter((item) => {
    const isSelectedRelatedObject = selectedRelatedObjectsValues?.length
      ? item.objectApiNames?.some((name) => selectedRelatedObjectsValues?.includes(name)) ||
        item.referencedObjects
          ?.filter((obj) => obj !== excludedFromReferenceObjectName)
          .some((name) => selectedRelatedObjectsValues?.includes(name))
      : true;

    const isSelectedConfigurationType = selectedConfigurationTypesValues?.length
      ? selectedConfigurationTypesValues?.includes(item.parentType)
      : true;

    const isActive = selectedActiveOnly ? isItemActive(item) : true;

    return isSelectedRelatedObject && isActive && isSelectedConfigurationType;
  });
};

// Cache for regex patterns to avoid recreating them
const regexCache = new Map<string, RegExp>();

// Get or create cached regex pattern
const getCachedRegex = (searchTxt: string): RegExp => {
  const searchKey = searchTxt.trim(); // Ensure we always work with trimmed strings
  if (!regexCache.has(searchKey)) {
    // Remove 'g' flag to avoid lastIndex state issues - we only use test() method
    regexCache.set(searchKey, new RegExp(escapeRegexp(searchKey), 'i'));

    // Limit cache size to prevent memory leaks
    if (regexCache.size > 100) {
      const firstKey = regexCache.keys().next().value;
      if (firstKey) {
        regexCache.delete(firstKey);
      }
    }
  }
  return regexCache.get(searchKey)!;
};

export const filterItemsByQuerySearch = ({
  itemProps,
  searchTxt,
  items,
}: {
  itemProps: string[];
  searchTxt: string | undefined;
  items?: any[];
}) => {
  // Early returns for performance
  if (!searchTxt?.trim()) return items;
  if (!items?.length) return items;

  // Type narrowing: at this point we know searchTxt is defined and has content
  const trimmedSearchTxt = (searchTxt as string).trim();
  const regexp = getCachedRegex(trimmedSearchTxt);

  // Use for loop for better performance than filter + some combination
  const filteredItems: any[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    // Check if any property matches, break early on first match
    for (let j = 0; j < itemProps.length; j++) {
      const propValue = item[itemProps[j]];

      if (isString(propValue) && regexp.test(propValue)) {
        filteredItems.push(item);
        break; // Found a match, no need to check other properties
      }
    }
  }

  return filteredItems;
};

export const rulePropsToFilter = ['label', 'name'];
export const fieldPropsToFilter = ['label', 'name'];
